import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  UserCredential,
  AuthError,
  ConfirmationResult
} from 'firebase/auth';
import { auth, googleProvider, appleProvider } from '../firebase';
import { activityLogService } from './activityLogService';

export interface AuthErrorWithCode extends AuthError {
  code: string;
}

export class AuthService {
  private recaptchaVerifier: RecaptchaVerifier | null = null;
  private confirmationResult: ConfirmationResult | null = null;


  // Email/Password Authentication
  async signInWithEmail(email: string, password: string): Promise<UserCredential> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      // Log the login activity
      await activityLogService.logUserLogin(userCredential.user.uid);
      return userCredential;
    } catch (error) {
      throw this.handleAuthError(error as AuthErrorWithCode);
    }
  }

  async signUpWithEmail(email: string, password: string, displayName?: string): Promise<UserCredential> {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      if (displayName) {
        await updateProfile(userCredential.user, { displayName });
      }
      
      // Log the sign-up activity
      await activityLogService.logUserSignUp(userCredential.user.uid);
      
      return userCredential;
    } catch (error) {
      throw this.handleAuthError(error as AuthErrorWithCode);
    }
  }

  async resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      throw this.handleAuthError(error as AuthErrorWithCode);
    }
  }

  // Google Authentication
  async signInWithGoogle(): Promise<UserCredential> {
    try {
      const userCredential = await signInWithPopup(auth, googleProvider);
      // Log the login activity
      await activityLogService.logUserLogin(userCredential.user.uid);
      return userCredential;
    } catch (error) {
      throw this.handleAuthError(error as AuthErrorWithCode);
    }
  }

  // Apple Authentication
  async signInWithApple(): Promise<UserCredential> {
    try {
      const userCredential = await signInWithPopup(auth, appleProvider);
      // Log the login activity
      await activityLogService.logUserLogin(userCredential.user.uid);
      return userCredential;
    } catch (error) {
      throw this.handleAuthError(error as AuthErrorWithCode);
    }
  }

  // Phone Authentication
  async signInWithPhone(phoneNumber: string, recaptchaContainerId: string): Promise<void> {
    try {
      // Clean up any existing reCAPTCHA
      this.destroyRecaptcha();

      // Validate phone number format
      const cleanPhoneNumber = this.formatPhoneNumber(phoneNumber);
      if (!cleanPhoneNumber) {
        throw new Error('Invalid phone number format. Please enter a valid phone number with country code (e.g., +1234567890)');
      }



      // Check if container exists and is empty
      const container = document.getElementById(recaptchaContainerId);
      if (!container) {
        throw new Error('reCAPTCHA container not found. Please refresh the page and try again.');
      }

      // Clear the container and wait for DOM to settle
      container.innerHTML = '';
      await new Promise(resolve => setTimeout(resolve, 200));

      // Create new recaptcha verifier with better error handling
      this.recaptchaVerifier = new RecaptchaVerifier(auth, recaptchaContainerId, {
        size: 'normal',
        callback: () => {
          console.log('reCAPTCHA solved successfully');
        },
        'expired-callback': () => {
          console.log('reCAPTCHA expired');
          this.destroyRecaptcha();
        }
      });

      // Render the reCAPTCHA with error handling
      try {
        await this.recaptchaVerifier.render();
        console.log('reCAPTCHA rendered successfully');
      } catch (renderError) {
        console.error('reCAPTCHA render error:', renderError);
        this.destroyRecaptcha();
        throw new Error('Failed to load reCAPTCHA. Please refresh the page and try again.');
      }

      // Wait for reCAPTCHA to be ready
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Check if reCAPTCHA is actually ready
      if (!this.recaptchaVerifier) {
        throw new Error('reCAPTCHA failed to initialize. Please refresh the page and try again.');
      }

      // Send verification code with better error handling
      try {
        this.confirmationResult = await signInWithPhoneNumber(auth, cleanPhoneNumber, this.recaptchaVerifier);
        console.log('Verification code sent successfully');
        
        // Hide the reCAPTCHA after successful verification code send
        this.hideRecaptcha();
      } catch (signInError) {
        console.error('Phone sign-in error:', signInError);
        
        // Handle specific Firebase configuration errors
        if (signInError && typeof signInError === 'object' && 'code' in signInError) {
          const firebaseError = signInError as AuthErrorWithCode;
          
          // Check for configuration issues
          if (firebaseError.code === 'auth/operation-not-allowed') {
            throw new Error('Phone authentication is not enabled for this project. Please use email or social login instead.');
          }
          
          if (firebaseError.code === 'auth/unsupported-first-factor') {
            throw new Error('Phone authentication is not supported in this environment. Please use email or social login instead.');
          }
          
          // Handle other specific errors
          throw this.handleAuthError(firebaseError);
        }
        
        // Handle generic errors
        throw new Error('Failed to send verification code. Please check your phone number and try again.');
      }
    } catch (error) {
      // Clean up on error
      this.destroyRecaptcha();
      
      // Re-throw the error
      throw error;
    }
  }

  async verifyPhoneCode(code: string): Promise<UserCredential> {
    try {
      if (!this.confirmationResult) {
        throw new Error('No phone verification in progress. Please start phone authentication first.');
      }
      
      const result = await this.confirmationResult.confirm(code);
      this.confirmationResult = null; // Clear the confirmation result
      this.destroyRecaptcha(); // Clean up reCAPTCHA
      
      // Log the login activity
      await activityLogService.logUserLogin(result.user.uid);
      
      return result;
    } catch (error) {
      this.destroyRecaptcha(); // Clean up on error
      throw this.handleAuthError(error as AuthErrorWithCode);
    }
  }

  // Helper method to format phone number
  private formatPhoneNumber(phoneNumber: string): string | null {
    if (!phoneNumber || typeof phoneNumber !== 'string') {
      return null;
    }

    // Remove all non-digit characters except +
    let cleaned = phoneNumber.replace(/[^\d+]/g, '');
    
    // Remove any leading zeros after the +
    cleaned = cleaned.replace(/^\+0+/, '+');
    
    // Ensure it starts with +
    if (!cleaned.startsWith('+')) {
      // If it doesn't start with +, assume it's a US number
      const digitsOnly = cleaned.replace(/\D/g, '');
      if (digitsOnly.length === 10) {
        cleaned = '+1' + digitsOnly;
      } else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
        cleaned = '+' + digitsOnly;
      } else {
        return null;
      }
    }
    
    // Validate the format (should be + followed by country code and number)
    const phoneRegex = /^\+[1-9]\d{1,14}$/;
    if (!phoneRegex.test(cleaned)) {
      return null;
    }
    
    // Additional validation for common country codes
    const countryCode = cleaned.substring(1, 3);
    const numberLength = cleaned.length - 1;
    
    // US/Canada: +1 followed by 10 digits
    if (countryCode === '1' && numberLength !== 11) {
      return null;
    }
    
    // UK: +44 followed by 10-11 digits
    if (countryCode === '44' && (numberLength < 12 || numberLength > 13)) {
      return null;
    }
    
    return cleaned;
  }

  // Error handling
  private handleAuthError(error: AuthErrorWithCode): Error {
    let message = 'An authentication error occurred';
    
    switch (error.code) {
      // Email/Password errors
      case 'auth/user-not-found':
        message = 'No account found with this email address';
        break;
      case 'auth/wrong-password':
        message = 'Incorrect password';
        break;
      case 'auth/invalid-email':
        message = 'Invalid email address';
        break;
      case 'auth/weak-password':
        message = 'Password should be at least 6 characters';
        break;
      case 'auth/email-already-in-use':
        message = 'An account with this email already exists';
        break;
      
      // Popup errors
      case 'auth/popup-closed-by-user':
        message = 'Sign-in was cancelled';
        break;
      case 'auth/popup-blocked':
        message = 'Pop-up was blocked. Please allow pop-ups for this site';
        break;
      case 'auth/popup-too-many-requests':
        message = 'Too many requests. Please try again later';
        break;
      
      // Network errors
      case 'auth/network-request-failed':
        message = 'Network error. Please check your internet connection';
        break;
      
      // Phone errors
      case 'auth/invalid-phone-number':
        message = 'Invalid phone number format. Please enter a valid phone number with country code (e.g., +1234567890)';
        break;
      case 'auth/invalid-verification-code':
        message = 'Invalid verification code. Please check the code and try again';
        break;
      case 'auth/invalid-verification-id':
        message = 'Invalid verification ID. Please try again';
        break;
      case 'auth/captcha-check-failed':
        message = 'reCAPTCHA verification failed. Please try again.';
        break;
      case 'auth/quota-exceeded':
        message = 'Too many verification attempts. Please try again later';
        break;
      case 'auth/too-many-requests':
        message = 'Too many verification attempts for this phone number. Please wait 1 hour before trying again, or use a different phone number.';
        break;
      case 'auth/unsupported-first-factor':
        message = 'Phone authentication is not enabled for this project. Please contact support.';
        break;
      case 'auth/operation-not-allowed':
        message = 'Phone authentication is not enabled. Please use email or social login instead.';
        break;
      
      // Apple specific errors
      case 'auth/account-exists-with-different-credential':
        message = 'An account already exists with the same email address but different sign-in credentials';
        break;
      
      default:
        message = error.message || message;
    }
    
    return new Error(message);
  }

  // Hide reCAPTCHA widget (but keep it for potential reuse)
  hideRecaptcha(): void {
    try {
      const container = document.getElementById('recaptcha-container');
      if (container) {
        container.style.display = 'none';
      }
    } catch (error) {
      console.warn('Error hiding reCAPTCHA:', error);
    }
  }

  // Show reCAPTCHA widget
  showRecaptcha(): void {
    try {
      const container = document.getElementById('recaptcha-container');
      if (container) {
        container.style.display = 'flex';
      }
    } catch (error) {
      console.warn('Error showing reCAPTCHA:', error);
    }
  }

  // Cleanup
  destroyRecaptcha(): void {
    try {
      if (this.recaptchaVerifier) {
        this.recaptchaVerifier.clear();
        this.recaptchaVerifier = null;
      }
      this.confirmationResult = null;
    } catch (error) {
      console.warn('Error cleaning up reCAPTCHA:', error);
    }
  }


}

export const authService = new AuthService();
